import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  retrieveAutomobileBrandsREST,
  retrieveAutomobileModelsByBrandREST,
  retrieveAutoMobileVersionsKwFactsBySeriesREST,
} from '../legacy/services/apiRouterService';
import { AutomobileBrandProps } from '../../../../types/VehicleResponse';
import SearchSelectInputV2 from './SearchSelectInputV2';
import { stringify } from 'query-string';
import cogoToast from 'cogo-toast';
import { isObjectEmpty } from '../utils/objectUtils';
import { Brand, Model, VehicleChoices } from '../../../../types/SMT';
import { mq } from '../utils/styledComponentsUtils';

// Constants

const DEBOUNCE_CONFIG = {
  time: 500,
  options: {
    leading: true,
  },
};

// Interfaces

interface SeriesProps {
  series: {
    brand_key: string;
    id: string;
    name: string;
  };
}

interface BrandOption {
  label: string;
  value: { key: string; logo: string };
}

interface Props {
  setBrand: (brand?: Brand) => void;
  setModel: (model?: Model) => void;
  setFuel: (fueltype?: string) => void;
  setKw: (kw?: number) => void;
  selectedValues: {
    brand?: Brand;
    model?: Model;
    fuelType?: string;
    kw?: number;
  };
  vehicleChoices?: VehicleChoices;
  setVehicleChoices?: React.Dispatch<
    React.SetStateAction<VehicleChoices | undefined>
  >;
  brandFilter?: string[];
  hasError?: {
    brand: boolean;
    model: boolean;
    fuel: boolean;
    kw: boolean;
  };
  setHasError?: React.Dispatch<
    React.SetStateAction<{
      brand: boolean;
      model: boolean;
      fuel: boolean;
      kw: boolean;
    }>
  >;
  categories?: Array<'PASSENGER_CAR' | 'LIGHT_COMMERCIAL_VEHICLE'>;
}

// Component

const VehicleLookupInputContainer: React.FC<React.PropsWithChildren<Props>> = ({
  setModel,
  setBrand,
  setFuel,
  setKw,
  selectedValues = {},
  vehicleChoices,
  setVehicleChoices,
  brandFilter,
  hasError,
  setHasError,
  categories,
}) => {
  const { t } = useTranslation();
  const [fixedBrand, setFixedBrand] = useState<BrandOption>();

  const fuelTypes = {
    PET: t('Vehicles.motor_types.PET'),
    DIE: t('Vehicles.motor_types.DIE'),
    LPG: t('Vehicles.motor_types.LPG'),
    ELE: t('Vehicles.motor_types.ELE'),
    HYB: t('Hybrid'),
    CNG: t('Vehicles.motor_types.CNG'),
  };

  const computeStringToQuery = (value: string): string => {
    const queryObj = {
      category: categories,
      q: value,
    };
    return stringify(queryObj, { skipEmptyString: true, skipNull: true });
  };

  const handleBrands = async (value: string): Promise<BrandOption[]> => {
    const { time, options } = DEBOUNCE_CONFIG;

    const debounceBrands = debounce(
      retrieveAutomobileBrandsREST,
      time,
      options,
    );
    const result = await debounceBrands(computeStringToQuery(value));

    if (result) {
      const [resp, status] = result;
      if (status !== 200) return [];
      const { items } = resp;
      const allOptions: BrandOption[] = items.map(
        ({ brand }: AutomobileBrandProps) => ({
          label: brand.name,
          value: { key: brand.key, logo: brand.logo_url },
        }),
      );
      return allOptions.filter((option: BrandOption) => {
        // check if brand can be shown
        if (brandFilter?.length === 0 || !brandFilter) return true;
        if (brandFilter?.includes(option?.value?.['key'])) {
          if (brandFilter?.length === 1) {
            setFixedBrand(option);
            handleBrandSelect(option.label, option.value);
          }
          return true;
        }
        return false;
      });
    }

    return [];
  };

  const handleModels = async (value: string) => {
    const { time, options } = DEBOUNCE_CONFIG;
    if (!selectedValues?.brand?.key) return undefined;

    const debounceModels = debounce(
      retrieveAutomobileModelsByBrandREST,
      time,
      options,
    );
    const result = await debounceModels(
      selectedValues?.brand?.key,
      computeStringToQuery(value),
    );

    if (result) {
      const [resp, status] = result;
      if (status !== 200) return null;
      const { items } = resp;
      return items.map(({ series }: SeriesProps) => ({
        label: series.name,
        value: {
          brandKey: series.brand_key,
          id: series.id,
          name: series.name,
          model: series.name,
        },
      }));
    }
  };

  const handleKws = async (val: string) => {
    const { time, options } = DEBOUNCE_CONFIG;
    const { fuelType, model, brand } = selectedValues;

    if (!selectedValues?.model) return undefined;

    const debounceKws = debounce(
      retrieveAutoMobileVersionsKwFactsBySeriesREST,
      time,
      options,
    );

    const query = {
      category: categories,
      kw: val,
      motor_type: fuelType === 'HYB' ? ['ELE_PET', 'ELE_DIE'] : fuelType,
    };

    const result = await debounceKws(
      brand?.key,
      model?.id,
      stringify(query, { skipEmptyString: true, skipNull: true }),
    );

    if (result) {
      const [resp, status] = result;
      if (status !== 200) return cogoToast.error("Could not get KW's");
      const { kw } = resp;
      return kw.map((kw: number) => ({
        label: kw,
        value: kw,
      }));
    }
  };

  const handleBrandSelect = (label: string, value: Brand) => {
    if (typeof value !== 'number' && typeof value !== 'string') {
      setVehicleChoices &&
        setVehicleChoices({ ...vehicleChoices, brand: { label, value } });

      setModel(undefined);
      setFuel(undefined);
      setKw(undefined);
      setBrand({ ...value, label });
    } else setBrand(undefined);
  };

  const handleModelSelect = (label: string, value: Model) => {
    if (typeof value !== 'number' && typeof value !== 'string') {
      setVehicleChoices &&
        setVehicleChoices({ ...vehicleChoices, model: { label, value } });

      setFuel(undefined);
      setKw(undefined);
      setModel(value);
    } else setModel(undefined);
  };

  const handleMotorSelect = (label: string, value: string) => {
    if (typeof value === 'string') {
      setVehicleChoices &&
        setVehicleChoices({ ...vehicleChoices, fuel: { label, value } });

      setKw(undefined);
      setFuel(value);
    } else setFuel(undefined);
  };

  const handleKwSelect = (label: string, value: number) => {
    if (typeof value === 'number') {
      setVehicleChoices &&
        setVehicleChoices({ ...vehicleChoices, kw: { label, value } });
      setKw(value);
    } else setKw(undefined);
  };
  return (
    <SelectInputContainer>
      <SearchSelectInputV2
        async
        name="brand"
        isClearable
        placeholder={t('SEARCH_OR_CHOOSE')}
        noOptionMessage={t('No options car brands')}
        loadOptions={handleBrands}
        onSelected={({ label, value }) => {
          if (!Array.isArray(value) && typeof value !== 'string')
            handleBrandSelect(label, value as Brand);
          setHasError && hasError && setHasError({ ...hasError, brand: false });
        }}
        disabled={!!fixedBrand}
        value={
          fixedBrand ||
          (vehicleChoices?.brand?.value?.key === selectedValues.brand?.key &&
            vehicleChoices?.brand) ||
          undefined
        }
        dataTestId={`vehicle_lookup_brand`}
        error={(hasError?.brand && t('required')) || ''}
      >
        {t('Vehicles.Brand')}
      </SearchSelectInputV2>
      <SearchSelectInputV2
        async
        name="model"
        disabled={
          isObjectEmpty(selectedValues?.brand) || !selectedValues?.brand
        }
        placeholder={t('SEARCH_OR_CHOOSE')}
        key={selectedValues?.brand?.key}
        loadOptions={(val) => handleModels(val)}
        isClearable
        onSelected={({ label, value }) => {
          if (!Array.isArray(value)) handleModelSelect(label, value as Model);
          setHasError && hasError && setHasError({ ...hasError, model: false });
        }}
        value={
          (vehicleChoices?.model?.value?.id === selectedValues.model?.id &&
            vehicleChoices?.model) ||
          undefined
        }
        dataTestId={`vehicle_lookup_model`}
        error={(hasError?.model && t('required')) || ''}
      >
        {t('Vehicles.Model')}
      </SearchSelectInputV2>
      <SearchSelectInputV2
        name="motorType"
        isClearable
        disabled={
          isObjectEmpty(selectedValues?.model) || !selectedValues?.model
        }
        placeholder={t('SEARCH_OR_CHOOSE')}
        key={'motor_' + selectedValues?.model?.id}
        options={Object.entries(fuelTypes).map(([key, value]) => ({
          label: value,
          value: key,
        }))}
        onSelected={({ label, value }) => {
          if (!Array.isArray(value)) handleMotorSelect(label, value as string);
        }}
        value={
          (vehicleChoices?.fuel?.value === selectedValues.fuelType &&
            vehicleChoices?.fuel) ||
          undefined
        }
        data-test-id={`vehicle_lookup_motor_type`}
      >
        {t('Vehicles.Fuel type')}
      </SearchSelectInputV2>
      <SearchSelectInputV2
        async
        name="kw"
        disabled={
          isObjectEmpty(selectedValues?.model) || !selectedValues?.model
        }
        placeholder={t('SEARCH_OR_CHOOSE')}
        key={'kw_' + selectedValues?.fuelType + selectedValues?.model?.id}
        loadOptions={(val) => handleKws(val)}
        isClearable
        onSelected={({ label, value }) => {
          if (!Array.isArray(value)) handleKwSelect(label, value as number);
        }}
        value={
          (vehicleChoices?.kw?.value === selectedValues.kw &&
            vehicleChoices?.kw) ||
          undefined
        }
        data-test-id={`vehicle_lookup_transmission_type`}
      >
        {t('kw')} ({t('kW')})
      </SearchSelectInputV2>
    </SelectInputContainer>
  );
};

const SelectInputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  > div {
    max-width: 100%;

    &:not(:first-of-type) {
      margin-top: 2.4rem;

      ${mq.mobileL} {
        margin-top: 0;
      }
    }

    ${mq.mobileL} {
      max-width: calc(50% - 1.6rem);
      margin-top: 0;
    }

    &:nth-last-of-type(2),
    &:last-of-type {
      margin-top: 2.4rem;

      ${mq.tablet} {
        margin-top: auto;
      }
    }
  }
`;

export default VehicleLookupInputContainer;
